import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useVariant } from '../../contexts/VariantContext';
import { useDomainManager } from '../../config';
import { getDomainImageAuto, handleImageErrorWithFallback } from '../../utils/imageManager';
import { renderCtaButton } from '../../utils/ctaHelper';
import { usePageTitle } from '../../hooks/usePageTitle';

const VariantSolutions = () => {
  const { brand, domainConfig, isLoading, cta } = useTheme();
  const { variant, styles } = useVariant();
  const domainManager = useDomainManager(brand?.id);
  
  // Get hostname-specific H1 title
  const h1Title = usePageTitle('solutions');

  // Show loading state while domain configuration is being loaded
  if (isLoading || !domainConfig || !brand?.id) {
    return (
      <div className="pt-16 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300">Loading solutions...</p>
        </div>
      </div>
    );
  }

  // Get domain-specific solutions using the unified configuration
  const getSolutionsContent = () => {
    
    const solutions = domainManager.getSolutions();
    
    
    if (solutions && solutions.sections && solutions.sections.length > 0) {
      // Add image URLs to each section
      const sectionsWithImages = solutions.sections.map((section, index) => ({
        ...section,
        image: getDomainImageAuto(brand.id, `solution-${index + 1}`, ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'])
      }));
      
      const result = {
        title: solutions.title,
        subtitle: solutions.subtitle,
        description: solutions.description,
        sections: sectionsWithImages
      };
      
      
      return result;
    }
    
    // If no solutions are configured, return null to hide the section
    return null;
  };

  const solutionsData = getSolutionsContent();
  
  // If no solutions are configured, show a fallback message
  if (!solutionsData) {
    return (
      <div className="pt-16 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">No Solutions Found</h1>
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            Brand ID: {brand?.id || 'undefined'}<br/>
            Domain Config ID: {domainConfig?.id || 'undefined'}<br/>
            Domain Manager ID: {domainManager?.id || 'undefined'}
          </p>
          <p className="text-gray-500 dark:text-gray-400">
            Check the browser console for debug information.
          </p>
        </div>
      </div>
    );
  }
  
  const { title, subtitle, description, sections } = solutionsData;
  


  // Use centralized variant styles
  const variantClasses = {
    container: styles.container,
    hero: styles.heroBg,
    card: styles.card,
    button: styles.button,
    buttonSecondary: styles.buttonSecondary,
    text: styles.text,
    textSecondary: styles.textSecondary,
    accent: styles.accent,
    section: styles.section
  };

  return (
    <div className={`pt-16 ${variantClasses.container}`}>
      {/* Hero Section */}
      <div className={`relative overflow-hidden ${variantClasses.hero}`}>
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-12 py-24">
          <div className="text-center">
            <h1 className={`text-4xl md:text-6xl font-bold mb-6 ${variantClasses.text}`}>
              {h1Title || title}
            </h1>
            <p className={`text-xl md:text-2xl mb-4 ${variantClasses.textSecondary}`}>
              {subtitle}
            </p>
            <p className={`text-lg max-w-3xl mx-auto ${variantClasses.textSecondary}`}>
              {description}
            </p>
          </div>
        </div>
        
        {/* Background Pattern */}
        <div className={`absolute inset-0 ${
          variant === 'modern' ? 'bg-gradient-to-br from-transparent via-white/5 to-transparent' :
          variant === 'creative' ? 'bg-gradient-to-br from-transparent via-white/10 to-transparent' :
          variant === 'elegant' ? 'bg-gradient-to-br from-transparent via-white/5 to-transparent' :
          'bg-gradient-to-br from-transparent via-white/5 to-transparent'
        }`}></div>
      </div>

      {/* Solutions Sections */}
      <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-12 py-20">
        <div className="max-w-7xl mx-auto">
          {sections.map((section, index) => (
            <section
              key={index}
              className={`mb-20 rounded-2xl shadow-lg overflow-hidden ${
                variantClasses.card
              } ${
                variant === 'creative' ? 'transform hover:rotate-1 transition-transform duration-300' :
                variant === 'elegant' ? 'hover:shadow-xl transition-shadow duration-300' :
                'hover:shadow-xl transition-shadow duration-300'
              }`}
            >
              <div className={`${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} flex flex-col lg:flex items-center`}>
                {/* Content */}
                <div className="flex-1 p-8 md:p-12">
                  <h2 className={`text-2xl md:text-3xl font-bold mb-6 ${variantClasses.accent}`}>
                    {section.title}
                  </h2>
                  
                  <div className="prose prose-lg max-w-none">
                    <p className={`leading-relaxed text-lg whitespace-pre-wrap ${variantClasses.textSecondary}`}>
                      {section.description}
                    </p>
                  </div>
                </div>

                {/* Image */}
                <div className="flex-1 w-full lg:w-auto p-4 lg:p-6">
                  <div className="relative h-80 lg:h-full min-h-[30vh] overflow-hidden rounded-xl flex items-center justify-center">
                    <img
                      src={section.image}
                      alt={section.title}
                      className="w-full h-full object-cover rounded-xl object-center"
                      onError={(e) => handleImageErrorWithFallback(e, brand.id, `solution-${index + 1}`)}
                    />
                  </div>
                </div>
              </div>
            </section>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className={`relative overflow-hidden ${
        variant === 'minimal' ? 'bg-gray-50' :
        variant === 'modern' ? 'bg-slate-800/50' :
        variant === 'corporate' ? 'bg-gray-50' :
        variant === 'creative' ? 'bg-white/50' :
        variant === 'elegant' ? 'bg-blue-50' :
        variant === 'landing' ? 'bg-gradient-to-r from-blue-800 to-purple-800' :
        variant === 'dark' ? 'bg-gray-800/50' :
        'bg-gray-50'
      }`}>
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-12 py-16">
          <div className="text-center">
            <h2 className={`text-3xl md:text-4xl font-bold mb-6 ${variantClasses.accent}`}>
              Ready to Get Started?
            </h2>
            <p className={`text-lg mb-8 max-w-2xl mx-auto ${variantClasses.textSecondary}`}>
              Contact our experts to discuss how our solutions can transform your business operations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {renderCtaButton(cta?.primary, 'Request Demo', `px-8 py-4 font-semibold rounded-lg transition-all duration-300 text-center ${variantClasses.button}`, domainConfig?.shopLink)}
              {renderCtaButton(cta?.secondary, 'Buy Now', `px-8 py-4 font-semibold rounded-lg transition-all duration-300 text-center ${variantClasses.buttonSecondary}`, domainConfig?.shopLink)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VariantSolutions;
